import { ArrowLeft, ArrowRight } from '@creditornot/cb-icons';
import { cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { useDirection } from '@creditornot/cb-provider';
import { Tag } from '@creditornot/cb-tag';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import PageHeader, { PageHeaderContainer, PageHeading } from 'common/components/PageHeader';
import { PageHeader as ExperiencePageHeader } from 'modules/experience-core';

import CampaignStatusTag from './CampaignStatusTag';
import { useCampaignModuleContext } from '../context';
import { useCampaignsRoutes } from '../hooks/useCampaignsRoutes';
import { CampaignStatus } from '../types';

type CampaignHeaderProps = {
  title: string;
  venueCount?: number;
  isBackLinkEnabled?: boolean;
  children?: React.ReactNode;
  status?: CampaignStatus;
};

const StyledCampaignsLink = styled(Link)`
  display: flex;
  gap: 8px;
  align-items: center;
  color: ${cbSemanticCssVars.textSecondary};
  cursor: pointer;
  margin-block-end: 4px;
  ${typographyCss.Body3({ weight: 'semiBold' })}
`;

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledHeadingWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StyledArrowLeft = styled(ArrowLeft)`
  fill: ${cbSemanticCssVars.textSecondary};
`;

const StyledArrowRight = styled(ArrowRight)`
  fill: ${cbSemanticCssVars.textSecondary};
`;

const CampaignHeader = ({ title, children, status, isBackLinkEnabled = false, venueCount }: CampaignHeaderProps) => {
  const campaignsRoutes = useCampaignsRoutes();
  const cbDirection = useDirection();
  const { entityType, isExperienceFlow } = useCampaignModuleContext();
  if (isExperienceFlow) {
    return (
      <ExperiencePageHeader
        title={title}
        enableSelector={!isBackLinkEnabled}
        {...(isBackLinkEnabled
          ? {
              backLink: campaignsRoutes.index,
              backLinkText: <FormattedMessage id="campaigns.title" />,
            }
          : {})}
      >
        {children}
      </ExperiencePageHeader>
    );
  }
  return isBackLinkEnabled ? (
    <PageHeaderContainer>
      <StyledHeader>
        <StyledCampaignsLink to={campaignsRoutes.index}>
          {cbDirection === 'ltr' ? <StyledArrowLeft width="12px" /> : <StyledArrowRight width="12px" />}
          <FormattedMessage id="campaigns.title" />
        </StyledCampaignsLink>

        <StyledHeadingWrapper>
          <PageHeading>{title}</PageHeading>
          {entityType !== 'merchant' && venueCount && venueCount > 1 && (
            <Tag variant="darkBlue">
              <FormattedMessage id="campaigns.group-campaign" />
            </Tag>
          )}
          {status && <CampaignStatusTag status={status} />}
        </StyledHeadingWrapper>
      </StyledHeader>
    </PageHeaderContainer>
  ) : (
    <PageHeader title={title}>{children}</PageHeader>
  );
};

export default CampaignHeader;
